/**
 * INTEL mode — backstage intelligence board.
 *
 * Live, public-read surfaces:
 *   • Service layer inventory + health
 *   • Agent ↔ Service ownership matrix
 *   • LLM policy matrix (allow/deny)
 *   • Crew work queues per agent
 *   • Polling scheduler tier overview
 *   • Run crew pipeline (button) → surfaces full trace
 */
import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import '../../styles/studio.css';
import './studio-shell.css';
import './intel-mode.css';
import ShowHeader from './ShowHeader';

export default function IntelMode({ onNavigate }) {
  const [services, setServices] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [llmPolicy, setLlmPolicy] = useState(null);
  const [crew, setCrew] = useState([]);
  const [polling, setPolling] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [runningCrew, setRunningCrew] = useState(false);
  // New runtime surfaces — wired to /api/runtime/* endpoints.
  const [runtimeAgents, setRuntimeAgents] = useState(null);
  const [runtimeSensors, setRuntimeSensors] = useState(null);
  const [truthPath, setTruthPath] = useState(null);
  const [producerEpisode, setProducerEpisode] = useState(null);
  const [runtimeHealth, setRuntimeHealth] = useState(null);

  const load = useCallback(async () => {
    const [s, m, l, c, p, rA, rS, tP, prod, rH] = await Promise.all([
      api.get('/api/internal/services/').catch(() => ({ data: null })),
      api.get('/api/internal/services/matrix').catch(() => ({ data: null })),
      api.get('/api/internal/llm-policy/matrix').catch(() => ({ data: null })),
      api.get('/api/crew/status').catch(() => ({ data: { crew: [] } })),
      api.get('/api/internal/polling/overview').catch(() => ({ data: null })),
      api.get('/api/runtime/agents/matrix').catch(() => ({ data: null })),
      api.get('/api/runtime/sensors').catch(() => ({ data: null })),
      api.get('/api/runtime/truth-path').catch(() => ({ data: null })),
      api.get('/api/runtime/producer').catch(() => ({ data: null })),
      api.get('/api/runtime/health').catch(() => ({ data: null })),
    ]);
    setServices(s.data); setMatrix(m.data); setLlmPolicy(l.data); setCrew(c.data?.crew || []); setPolling(p.data);
    setRuntimeAgents(rA.data); setRuntimeSensors(rS.data); setTruthPath(tP.data);
    setProducerEpisode(prod.data?.lastEpisode || null);
    setRuntimeHealth(rH.data);
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const runCrew = async () => {
    setRunningCrew(true);
    try {
      // Kick the new runtime producer pipeline first so Studio Intel always
      // has a real episode packet to render. Fall back to the legacy crew run.
      const runtimeRes = await api.post('/api/runtime/producer/run').catch(() => null);
      const legacyRes = await api.post('/api/crew/run', {}).catch(() => null);
      setLastRun({
        ...(legacyRes?.data || {}),
        runtimeEpisode: runtimeRes?.data?.episode || null,
        runtimeOk: runtimeRes?.data?.ok ?? false,
        runtimeReason: runtimeRes?.data?.reason || null,
      });
      await load();
    } catch (e) {
      setLastRun({ error: e.message || String(e) });
    } finally {
      setRunningCrew(false);
    }
  };

  return (
    <div className="studio-shell intel-shell" data-testid="intel-mode">
      <ShowHeader mode="intel" onNavigate={onNavigate} status="PREP" episode={null} sensorSummary={null} nextSpeaker="host" />

      <div className="intel-grid" data-testid="intel-grid">
        {/* AGENT LAYER — runtime registered agents */}
        <section className="intel-block" data-testid="intel-agent-layer">
          <header>Agent Layer <em>{runtimeAgents?.totalRegistered ?? 0} registered</em></header>
          <div className="intel-body">
            {(runtimeAgents?.agents || []).map((a) => (
              <div className="intel-row" key={a.id} data-testid={`intel-agent-${a.id}`}>
                <span className="label">{(a.name || a.id).toUpperCase()}</span>
                <span className={`chip h-${a.serviceStatus === 'active' ? 'live' : a.serviceStatus === 'degraded' ? 'degraded' : 'unknown'}`}>{a.serviceStatus}</span>
                <span className="sub">{(a.domains || []).join(' · ') || a.role}</span>
              </div>
            ))}
            {(!runtimeAgents || !runtimeAgents.agents?.length) && (
              <div className="intel-row"><span className="sub">0 registered agents — waiting for runtime bootstrap</span></div>
            )}
          </div>
        </section>

        {/* SERVICES */}
        <section className="intel-block">
          <header>Service Layer <em>{services?.count ?? 0} registered</em></header>
          <div className="intel-body">
            {(services?.services || []).map((s) => (
              <div className="intel-row" key={s.service_id} data-testid={`intel-svc-${s.service_id}`}>
                <span className="label">{s.label}</span>
                <span className={`chip h-${s.health}`}>{s.health}</span>
                <span className="sub">{(s.domains || []).join(' · ')}</span>
              </div>
            ))}
          </div>
        </section>

        {/* AGENT → SERVICE MATRIX (runtime) */}
        <section className="intel-block" data-testid="intel-service-matrix">
          <header>Agent → Service Matrix <em>{runtimeAgents?.totalRegistered ?? 0}</em></header>
          <div className="intel-body">
            {(runtimeAgents?.agents || []).map((a) => (
              <div className="intel-row" key={a.id} data-testid={`intel-matrix-${a.id}`}>
                <span className="label">{(a.name || a.id).toUpperCase()}</span>
                <span className={`chip h-${a.serviceStatus === 'active' ? 'live' : a.serviceStatus === 'degraded' ? 'degraded' : 'unknown'}`}>{a.serviceStatus}</span>
                <span className="sub">
                  {(a.capabilities || []).join(' · ') || '—'} · LLM {a.llmAllowed ? 'on' : 'off'} · truth {a.truthPathAccess ? 'on' : 'off'}
                </span>
              </div>
            ))}
            {(!runtimeAgents || !runtimeAgents.agents?.length) && matrix && Object.entries(matrix).map(([agent, sids]) => (
              <div className="intel-row" key={agent} data-testid={`intel-matrix-${agent}`}>
                <span className="label">{agent.toUpperCase()}</span>
                <span className="sub">{sids.length === 0 ? '— no direct access' : sids.join(', ')}</span>
              </div>
            ))}
          </div>
        </section>

        {/* LLM POLICY — real counts + truth path */}
        <section className="intel-block" data-testid="intel-llm-policy">
          <header>
            LLM Policy <em>{runtimeAgents?.llmAllowedCount ?? (llmPolicy?.allowed?.length ?? 0)} allowed</em>
          </header>
          <div className="intel-body">
            {(runtimeAgents?.agents || []).map((a) => (
              <div className="intel-row" key={`llm-${a.id}`}>
                <span className="label" style={{ color: a.llmAllowed ? 'var(--status-nominal)' : 'var(--status-critical)' }}>
                  {a.id} · {a.role}
                </span>
                <span className="sub">{a.llmAllowed ? 'LLM allowed' : 'LLM disabled — deterministic only'}</span>
              </div>
            ))}
            <div className="intel-row" data-testid="intel-truth-path">
              <span className="label" style={{ color: truthPath?.allowed ? 'var(--status-nominal)' : 'var(--status-critical)' }}>
                TRUTH PATH · {truthPath?.status || 'UNKNOWN'}
              </span>
              <span className="sub">
                {truthPath?.allowed
                  ? `Band1=${truthPath.band1Count} routed=${truthPath.routedCount} interp=${truthPath.interpretationCount}`
                  : (truthPath?.reasons || []).join(' · ') || '—'}
              </span>
            </div>
            {runtimeHealth && (
              <div className="intel-row" data-testid="intel-runtime-health">
                <span
                  className="label"
                  style={{
                    color:
                      runtimeHealth.storeHealth?.status === 'live'
                        ? 'var(--status-nominal)'
                        : runtimeHealth.storeHealth?.status === 'degraded'
                        ? 'var(--status-warning, #fbbf24)'
                        : runtimeHealth.storeHealth?.status === 'offline'
                        ? 'var(--status-critical)'
                        : 'rgba(200,210,230,0.55)',
                  }}
                >
                  RUNTIME · {runtimeHealth.runtimeStarted ? 'STARTED' : 'OFFLINE'} · store {runtimeHealth.storeHealth?.status || 'unknown'}
                </span>
                <span className="sub">
                  hydrated {runtimeHealth.hydrated ? 'yes' : 'no'}
                  {runtimeHealth.storeHealth?.writesOk != null
                    ? ` · writes ${runtimeHealth.storeHealth.writesOk}/${runtimeHealth.storeHealth.writesOk + (runtimeHealth.storeHealth.writesFailed || 0)}`
                    : ''}
                  {(runtimeHealth.warnings || []).length ? ` · ${(runtimeHealth.warnings || []).join(' · ')}` : ''}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* POLLING — real sensor summary */}
        <section className="intel-block" data-testid="intel-polling">
          <header>
            Polling Scheduler <em>{runtimeSensors?.totalSensors ?? (polling?.summary?.total ?? 0)} sensors</em>
          </header>
          <div className="intel-body">
            {runtimeSensors?.byPollClass && Object.entries(runtimeSensors.byPollClass).map(([cls, counts]) => (
              <div className="intel-row" key={cls}>
                <span className="label">{cls.replace(/_/g, ' ')}</span>
                <span className={`chip h-${counts.live > 0 ? 'live' : 'degraded'}`}>{counts.live}/{counts.total}</span>
                <span className="sub">{counts.active} active</span>
              </div>
            ))}
            {runtimeSensors && (
              <div className="intel-row">
                <span className="label">overall</span>
                <span className={`chip h-${runtimeSensors.live > 0 ? 'live' : 'degraded'}`}>
                  {runtimeSensors.live}/{runtimeSensors.totalSensors}
                </span>
                <span className="sub">
                  deg {runtimeSensors.degraded} · stale {runtimeSensors.stale} · off {runtimeSensors.offline}
                </span>
              </div>
            )}
            {!runtimeSensors && polling?.summary?.by_tier && Object.entries(polling.summary.by_tier).map(([tier, counts]) => (
              <div className="intel-row" key={tier}>
                <span className="label">{tier.replace('_', ' ')}</span>
                <span className={`chip h-${counts.degraded > 0 ? 'degraded' : 'live'}`}>{counts.healthy}/{counts.count}</span>
                <span className="sub">{counts.degraded > 0 ? `${counts.degraded} degraded` : 'all healthy'}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CREW QUEUES */}
        <section className="intel-block">
          <header>Crew Work Queues</header>
          <div className="intel-body">
            {crew.map((c) => (
              <div className="intel-row" key={c.name}>
                <span className="label" style={{ color: c.color }}>{c.name.toUpperCase()}</span>
                <span className={`chip h-${c.status === 'working' ? 'live' : c.status === 'blocked' ? 'degraded' : 'unknown'}`}>{c.status}</span>
                <span className="sub">queue {c.queue_depth} · {c.unresolved} unresolved</span>
              </div>
            ))}
          </div>
        </section>

        {/* CREW RUN */}
        <section className="intel-block" data-testid="intel-crew-run">
          <header>Crew Pipeline <em>Wire → Scribe → Mercury → Producer → Quill → Frame → Stenographer</em></header>
          <div className="intel-body">
            <button className="intel-run-btn" onClick={runCrew} disabled={runningCrew} data-testid="intel-run-btn">
              {runningCrew ? 'Running…' : 'Run one end-to-end pass'}
            </button>
            {lastRun?.runtimeEpisode && (
              <div className="intel-run-trace" data-testid="intel-runtime-episode">
                <div className="run-head">
                  episode {String(lastRun.runtimeEpisode.episodeId).slice(0, 8)} · lead {lastRun.runtimeEpisode.leadHost} · next {lastRun.runtimeEpisode.nextHost}
                </div>
                <div className="run-head" style={{ opacity: 0.7 }}>
                  topic: {lastRun.runtimeEpisode.topic?.headline || lastRun.runtimeEpisode.topic?.domain || '—'}
                </div>
                {(lastRun.runtimeEpisode.segment || []).map((row, i) => (
                  <div className="run-stage" key={`seg-${i}`}>
                    <span className="stage-idx">{String(row.order).padStart(2, '0')}</span>
                    <span className="stage-agent">{String(row.host || '').toUpperCase()}</span>
                    <span className="stage-kind">{row.name}</span>
                  </div>
                ))}
              </div>
            )}
            {producerEpisode && !lastRun?.runtimeEpisode && (
              <div className="intel-run-trace" data-testid="intel-last-runtime-episode">
                <div className="run-head">
                  last episode {String(producerEpisode.episodeId).slice(0, 8)} · lead {producerEpisode.leadHost}
                </div>
                <div className="run-head" style={{ opacity: 0.7 }}>
                  topic: {producerEpisode.topic?.headline || producerEpisode.topic?.domain || '—'}
                </div>
              </div>
            )}
            {lastRun && !lastRun.error && (lastRun.trace || []).length > 0 && (
              <div className="intel-run-trace" data-testid="intel-run-trace">
                <div className="run-head">run {lastRun.run_id} · {lastRun.stages} stages</div>
                {(lastRun.trace || []).map((t, i) => (
                  <div className="run-stage" key={i}>
                    <span className="stage-idx">{String(i + 1).padStart(2, '0')}</span>
                    <span className="stage-agent">{t.agent.toUpperCase()}</span>
                    <span className="stage-kind">{t.kind}</span>
                  </div>
                ))}
              </div>
            )}
            {lastRun?.runtimeReason && !lastRun?.runtimeEpisode && (
              <div style={{ color: 'var(--status-critical)', padding: 12 }}>runtime: {lastRun.runtimeReason}</div>
            )}
            {lastRun?.error && <div style={{ color: 'var(--status-critical)', padding: 12 }}>error: {lastRun.error}</div>}
          </div>
        </section>
      </div>
    </div>
  );
}
